import { useMemo, useState } from "react";
import { UserPlus } from "lucide-react";
import Button from "../ui/Button";

function eligibleCases(rows, intent) {
  const wantAssigned = intent === "reassign";
  return (rows || [])
    .filter((row) => row.status !== "closed" && Boolean(row.assignedAgent) === wantAssigned)
    .slice()
    .sort((a, b) => {
      const left = a.numericId ?? a.id;
      const right = b.numericId ?? b.id;
      return String(left).localeCompare(String(right), undefined, { numeric: true });
    });
}

function wholeNumber(value) {
  const parsed = Number.parseInt(String(value ?? "").trim(), 10);
  return Number.isFinite(parsed) ? parsed : null;
}

export function pickCasesToAssign(rows, { mode, quantity, rangeFrom, rangeTo, intent = "assign" }) {
  const pool = eligibleCases(rows, intent);
  const max = pool.length;
  if (!max) {
    return {
      rows: [],
      error: intent === "reassign" ? "No assigned cases in this list." : "No unassigned cases in this list.",
    };
  }

  if (mode === "range") {
    const from = wholeNumber(rangeFrom);
    const to = wholeNumber(rangeTo);
    if (from == null || to == null || from < 1 || to < from || to > max) {
      return { rows: [], error: `Enter a range between 1 and ${max}.` };
    }
    return { rows: pool.slice(from - 1, to), error: null };
  }

  const count = wholeNumber(quantity);
  if (count == null || count < 1 || count > max) {
    return { rows: [], error: `Enter a quantity from 1 to ${max}.` };
  }
  return { rows: pool.slice(0, count), error: null };
}

export default function AssignQuantityBar({ rows, disabled, onAssign, intent = "assign" }) {
  const reassign = intent === "reassign";
  const max = useMemo(() => eligibleCases(rows, intent).length, [rows, intent]);
  const [mode, setMode] = useState("quantity");
  const [quantity, setQuantity] = useState("");
  const [rangeFrom, setRangeFrom] = useState("1");
  const [rangeTo, setRangeTo] = useState("");
  const [error, setError] = useState("");

  const submit = () => {
    const result = pickCasesToAssign(rows, { mode, quantity, rangeFrom, rangeTo, intent });
    if (result.error) {
      setError(result.error);
      return;
    }
    setError("");
    onAssign(result.rows);
  };

  return (
    <div className="mb-4 rounded-xl border border-primary/[0.08] bg-primary/[0.02] px-4 py-3">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="text-xs font-semibold text-primary">
            {reassign ? "Reassign assigned cases" : "Assign unassigned cases"}
          </p>
          <p className="mt-0.5 text-[11px] text-primary/50">
            <span className="font-bold text-primary">{max}</span> {reassign ? "assigned" : "unassigned"}
            {rows?.length ? ` of ${rows.length} in this list` : ""}. Quantity takes the first{" "}
            {reassign ? "assigned" : "unassigned"} cases. A range uses positions 1 to {max || 0}.
          </p>
        </div>
        <div className="flex rounded-lg bg-white p-0.5 ring-1 ring-primary/10">
          {[
            ["quantity", "Quantity"],
            ["range", "Range"],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              onClick={() => {
                setMode(key);
                setError("");
              }}
              className={`rounded-md px-3 py-1.5 text-xs font-semibold ${
                mode === key ? "bg-primary text-white" : "text-primary/55"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-end gap-2">
        {mode === "quantity" ? (
          <label className="block text-[11px] font-semibold text-primary/55">
            Quantity
            <input
              type="number"
              min={1}
              max={max || 1}
              value={quantity}
              disabled={!max || disabled}
              onChange={(event) => setQuantity(event.target.value)}
              placeholder={max ? `1–${max}` : "0"}
              className="mt-1 block w-28 rounded-lg border border-primary/15 bg-white px-3 py-2 text-sm font-semibold text-primary outline-none focus:border-primary/40"
            />
          </label>
        ) : (
          <>
            <label className="block text-[11px] font-semibold text-primary/55">
              From
              <input
                type="number"
                min={1}
                max={max || 1}
                value={rangeFrom}
                disabled={!max || disabled}
                onChange={(event) => setRangeFrom(event.target.value)}
                className="mt-1 block w-24 rounded-lg border border-primary/15 bg-white px-3 py-2 text-sm font-semibold text-primary outline-none focus:border-primary/40"
              />
            </label>
            <label className="block text-[11px] font-semibold text-primary/55">
              To
              <input
                type="number"
                min={1}
                max={max || 1}
                value={rangeTo}
                disabled={!max || disabled}
                onChange={(event) => setRangeTo(event.target.value)}
                placeholder={max ? String(max) : "0"}
                className="mt-1 block w-24 rounded-lg border border-primary/15 bg-white px-3 py-2 text-sm font-semibold text-primary outline-none focus:border-primary/40"
              />
            </label>
          </>
        )}
        <Button size="sm" disabled={!max || disabled} onClick={submit}>
          <UserPlus className="h-4 w-4" />
          {reassign ? "Reassign these cases" : "Assign these cases"}
        </Button>
      </div>
      {error ? <p className="mt-2 text-xs font-medium text-rose-700">{error}</p> : null}
    </div>
  );
}
